/**
 * @fileoverview Framework-agnostic rendering primitives shared by the document
 * preview (web canvas) and the PDF generation pipeline (worker).
 * @module document-render/primitives
 *
 * Responsibilities: - Provide ONE implementation of the rendering bits that were
 *                     previously duplicated/divergent across the FE preview and the
 *                     BE worker: checkbox HTML, boolean (pass/fail) wording,
 *                     compliance-status color, and document status colors.
 * Dependencies:     - none (pure string/util functions; emits HTML strings).
 * Architecture:     - Lives in @firesquirrel/document-render so both the Node worker
 *                     and the React canvas render identically (closes G-FID-02/03/04/05,
 *                     G-DRY-COLORS by construction). No framework imports.
 * Notes:            - `renderCheckboxHtml` uses only CSS properties permitted by the
 *                     shared sanitize-html allowlist in documentHtmlSanitizer.ts so
 *                     that checkbox spans survive sanitization intact in the PDF path.
 *                     `background` shorthand → `background-color` (only the longhand
 *                     is in the allowlist). `display` and `margin:auto` are now also
 *                     in the allowlist (restricted to safe values).
 */

// CheckboxMarkStyle is defined once in documentConfig (the canonical type module).
// Type-only import — erased at compile time, so this keeps the "no framework imports" rule.
import type { CheckboxMarkStyle } from './documentConfig';

/**
 * @process renderCheckboxHtml
 * Steps:    Return a CSS-styled checkbox span (filled when checked, empty otherwise).
 *           All inline styles use only properties permitted by the shared sanitize-html
 *           allowlist (documentHtmlSanitizer.ts) so the output survives sanitization
 *           in the worker PDF path without losing its visual appearance.
 * Inputs:   `checked` boolean.
 * Outputs:  Inline-styled HTML string. Identical in preview and PDF.
 * Idempotency: REQUIRED (pure).
 */
export function renderCheckboxHtml(checked: boolean): string {
  return checked
    ? '<span style="display:inline-block;width:14px;height:14px;background-color:#222;border:1.5px solid #222;border-radius:2px;vertical-align:middle;"><span style="display:block;width:6px;height:6px;background-color:#fff;border-radius:1px;margin:3px auto;"></span></span>'
    : '<span style="display:inline-block;width:14px;height:14px;background-color:#fff;border:1.5px solid #444;border-radius:2px;vertical-align:middle;"></span>';
}

/**
 * @contract VIOLATION_RESULT_COLORS
 * Purpose: Canonical default pass/fail colors for violation/checklist conditional
 *          coloring (G-VIOL-COLOR). Used when a template does not override them.
 * Fields:  pass (green-600), fail (red-600).
 * Rules:   Single source shared by preview + PDF; UI seeds its color pickers from here.
 * Sync:    N/A (pure rendering constant).
 */
export const VIOLATION_RESULT_COLORS = {
  pass: '#16a34a', // green-600
  fail: '#dc2626', // red-600
} as const;

// Re-exported so primitives consumers can type the mark style from this module too.
export type { CheckboxMarkStyle };

// Status keywords that mean an item passed / was corrected. Matched on whole words
// (see statusMatches) so 'complete' never matches 'incomplete' and 'ok' never matches 'revoked'.
const PASS_STATUS_KEYWORDS = [
  'pass', 'passed', 'compliant', 'resolved', 'closed', 'corrected', 'complete', 'completed', 'abated', 'satisfactory', 'ok', 'okay',
];
// Status keywords that explicitly mean an item failed / is outstanding (whole-word match).
const FAIL_STATUS_KEYWORDS = [
  'fail', 'failed', 'non-compliant', 'noncompliant', 'open', 'violation', 'active', 'pending', 'outstanding', 'unresolved', 'incomplete',
];

// Whole-word, case-insensitive match: 'ok' matches "OK"/"Status: OK" but not "Revoked"; 'complete'
// matches "Complete" but not "Incomplete". Keywords are plain words/hyphenated words (no regex metachars).
function statusMatches(status: string, keywords: string[]): boolean {
  return keywords.some((k) => new RegExp(`(^|[^a-z])${k}([^a-z]|$)`, 'i').test(status));
}

/**
 * @process violationItemPassed
 * Steps:    Derive an item's pass/fail result for conditional coloring. Precedence:
 *           explicit `passed` → `status` keyword match (compliant statuses pass,
 *           non-compliant statuses fail) → fall back to `!checked` (a checked
 *           violation box means the violation is present = failed).
 * Inputs:   item with optional `passed`, `status`, and `checked` fields.
 * Outputs:  boolean — true when the item passed.
 * Idempotency: REQUIRED (pure).
 */
export function violationItemPassed(item: {
  passed?: boolean;
  status?: string;
  checked?: boolean;
}): boolean {
  if (typeof item.passed === 'boolean') return item.passed;
  const status = (item.status ?? '').trim().toLowerCase();
  if (status) {
    if (statusMatches(status, FAIL_STATUS_KEYWORDS)) return false;
    if (statusMatches(status, PASS_STATUS_KEYWORDS)) return true;
  }
  return item.checked === false;
}

/**
 * @process renderViolationMarkHtml
 * Steps:    Render the mark glyph for a violation/checklist item according to the
 *           chosen style. 'box' keeps the legacy filled/empty square (no result color).
 *           'check'/'cross' render a single glyph, colored by result when coloring is on.
 *           'checkCross' renders a green ✓ for passing items and a red ✗ for failing ones.
 *           All inline styles stay within the shared sanitize-html allowlist.
 * Inputs:   `passed` boolean, `style` CheckboxMarkStyle, opts: `checked` (for box),
 *           `colored` (apply pass/fail color), `passColor`, `failColor`.
 * Outputs:  Inline-styled HTML string. Identical in preview and PDF.
 * Idempotency: REQUIRED (pure).
 */
export function renderViolationMarkHtml(
  passed: boolean,
  style: CheckboxMarkStyle = 'box',
  opts: { checked?: boolean; colored?: boolean; passColor?: string; failColor?: string } = {},
): string {
  const passColor = opts.passColor || VIOLATION_RESULT_COLORS.pass;
  const failColor = opts.failColor || VIOLATION_RESULT_COLORS.fail;

  if (style === 'box') {
    return renderCheckboxHtml(opts.checked !== false);
  }

  const glyph = style === 'cross' || (style === 'checkCross' && !passed) ? '&#10007;' : '&#10003;';
  // checkCross always colors by result; check/cross color only when result coloring is enabled.
  const useColor = style === 'checkCross' || opts.colored === true;
  const color = useColor ? (passed ? passColor : failColor) : '#222222';
  return `<span class="violation-mark" style="display:inline-block;width:14px;font-weight:bold;color:${color};vertical-align:middle;">${glyph}</span>`;
}

/**
 * @contract BooleanDisplayStyle
 * Purpose: How a boolean field value is rendered in a document.
 * Fields:  'yesNo' | 'passFail' | 'checkedUnchecked'
 * Rules:   The template author picks the style; preview and PDF must agree.
 * Sync:    N/A (pure rendering choice).
 */
export type BooleanDisplayStyle = 'yesNo' | 'passFail' | 'checkedUnchecked' | 'custom';

/**
 * @contract BooleanDisplayLabels
 * Purpose: Custom true/false labels for the 'custom' boolean style.
 * Fields:  trueLabel?, falseLabel?
 * Rules:   Defaults to 'Yes'/'No' when omitted.
 * Sync:    N/A.
 */
export interface BooleanDisplayLabels {
  trueLabel?: string;
  falseLabel?: string;
}

/**
 * @process formatBooleanDisplay
 * Steps:    Render a boolean using the chosen style so the FE preview and the BE PDF
 *           produce the SAME output (closes the "Pass/Fail" vs "Yes/No" divergence).
 * Inputs:   `value` boolean, `style` BooleanDisplayStyle (default 'yesNo'), optional
 *           custom `labels` (used only when style === 'custom').
 * Outputs:  Display string/HTML.
 * Idempotency: REQUIRED (pure).
 */
export function formatBooleanDisplay(
  value: boolean,
  style: BooleanDisplayStyle = 'yesNo',
  labels?: BooleanDisplayLabels,
): string {
  switch (style) {
    case 'passFail':
      return value ? 'Pass' : 'Fail';
    case 'checkedUnchecked':
      return renderCheckboxHtml(value);
    case 'custom':
      return value ? (labels?.trueLabel ?? 'Yes') : (labels?.falseLabel ?? 'No');
    case 'yesNo':
    default:
      return value ? 'Yes' : 'No';
  }
}

/**
 * @process complianceStatusColor
 * Steps:    Map a compliance status to its canonical hex color so the preview and
 *           the PDF color it identically (closes the "color in preview, plain in PDF"
 *           divergence — both now use this single source).
 * Inputs:   `status` string (e.g., 'Compliant', 'Non-Compliant').
 * Outputs:  Hex color string.
 * Idempotency: REQUIRED (pure).
 */
export function complianceStatusColor(status: string | undefined | null): string {
  if (status === 'Compliant') return '#16a34a'; // green-600
  if (status === 'Non-Compliant') return '#dc2626'; // red-600
  return '#374151'; // gray-700 (neutral / unknown)
}

/**
 * @contract DocumentStatusColor
 * Purpose: Canonical color for a generated-document lifecycle status, used by the
 *          management page and any status badge (closes G-DRY-COLORS — the two
 *          GeneratedDocumentsPage files previously disagreed).
 * Fields:  bg (hex), text (hex).
 * Rules:   This is the single source; UI maps to its own class/style as needed.
 * Sync:    N/A.
 */
export interface DocumentStatusColor {
  bg: string;
  text: string;
}

/**
 * @contract DOCUMENT_STATUS_COLORS
 * Purpose: One status→color map for the whole document area.
 * Fields:  Template publish lifecycle (draft, published) and generated-document
 *          delivery lifecycle (generated, sent, delivered, failed).
 * Rules:   Decision D13-R — single-author lifecycle; no multi-user approval states.
 * Sync:    N/A.
 */
export const DOCUMENT_STATUS_COLORS: Record<string, DocumentStatusColor> = {
  // Template publish lifecycle
  draft: { bg: '#f3f4f6', text: '#374151' },
  published: { bg: '#dcfce7', text: '#15803d' },
  // Generated-document delivery lifecycle
  generated: { bg: '#f3f4f6', text: '#374151' },
  sent: { bg: '#f3e8ff', text: '#7e22ce' },
  delivered: { bg: '#dcfce7', text: '#15803d' },
  failed: { bg: '#fee2e2', text: '#b91c1c' },
};
